import { useEffect, useState } from "react";

import { isPresent, noop } from "neetocist";
import useHotkeys from "neetohotkeys";
import { Help } from "neetoicons";
import { Profile } from "neetoteam";
import { Avatar, Dropdown } from "neetoui";
import { not } from "ramda";

import KeyboardShortcuts from "components/KeyboardShortcuts";
import SubscriptionUpgradeRequestModal from "components/SubscriptionUpgradeRequestModal";

import ProductSwitcherModal from "./ProductSwitcher/Modal";
import ProfileMenu from "./ProfileMenu";

import { DEFAULT_MENU_LINK_PROPS } from "../constants";
import { getDefaultBottomLinks, getProfileInfo } from "../utils";

const ProfileButton = ({
  profileInfoOverrides,
  showProductSwitcher,
  bottomLinks,
  topLinks,
  customContent,
  isThemeSwitcherEnabled,
  isOrganizationSwitcherEnabled,
  profilePopoverProps,
  isConsumer,
  enableSubscriptionUpgradeRequest,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isBackdropVisible, setIsBackdropVisible] = useState(false);
  const [changesCount, setChangesCount] = useState(null);

  const [, setIsOpen] = KeyboardShortcuts.usePaneState();

  useHotkeys(["ctrl+u", "command+u"], () => setIsModalOpen(not), {
    mode: "global",
  });

  const profileInfo = getProfileInfo(profileInfoOverrides, isConsumer);

  useEffect(() => {
    const Nv = {
      onReceiveData: data => {
        if (!data.unreadCount) return;
        setChangesCount(data.unreadCount);
      },
      onShow: () => {
        setChangesCount(null);
      },
    };

    window.Nv = window.Nv ? Object.assign(window.Nv, Nv) : Nv;
  }, []);

  const newBottomLinks = [
    ...getDefaultBottomLinks(profileInfo.isAuthenticated, isConsumer),
    ...bottomLinks,
  ];

  const defaultLinks = Object.keys(DEFAULT_MENU_LINK_PROPS)
    .map(key => ({
      ...DEFAULT_MENU_LINK_PROPS[key],
      ...(key === "keyboardShortcutProps" && {
        onClick: () => setIsOpen(prev => !prev),
      }),
      ...(key === "engageProps" && changesCount && { changesCount }),
    }))
    .filter(({ hide }) => !hide);

  return (
    <>
      <Profile.Pane popoverProps={profilePopoverProps} />
      {isBackdropVisible && (
        <div className="neeto-molecules-floating-action-button__backdrop" />
      )}
      <Dropdown
        appendTo={() => document.body}
        buttonStyle="link"
        closeOnSelect={false}
        icon={noop}
        interactiveDebounce={30}
        offset={[4, 4]}
        position="right-end"
        strategy="fixed"
        theme="light neeto-molecules-floating-action-outer-dropdown-wrapper"
        trigger="all"
        zIndex={99999}
        customTarget={
          <button
            className="neeto-molecules-floating-action-button"
            type="button"
          >
            <Avatar
              className="neeto-ui-flex-shrink-0 neeto-molecules-floating-action-button__profile-avatar"
              data-testid="profile-avatar"
              size="medium"
              user={profileInfo}
            />
            <span className="neeto-molecules-floating-action-button__help-icon">
              <Help size={20} />
            </span>
          </button>
        }
        dropdownProps={{
          classNames: "neeto-molecules-floating-action-button__dropdown",
        }}
        onHide={() => setIsBackdropVisible(false)}
        onShow={() => setIsBackdropVisible(true)}
      >
        <ProfileMenu
          {...{
            customContent,
            defaultLinks,
            enableSubscriptionUpgradeRequest,
            isConsumer,
            isOrganizationSwitcherEnabled,
            isThemeSwitcherEnabled,
            profileInfo,
            showProductSwitcher,
            topLinks,
          }}
          bottomLinks={newBottomLinks}
          openUpgradeModal={() => setIsUpgradeModalOpen(true)}
          toggleModal={setIsModalOpen}
        />
      </Dropdown>
      {isPresent(changesCount) && (
        <span
          className="neeto-molecules-menu__notif-count"
          data-testid="help-icon-changes-count"
        >
          {changesCount}
        </span>
      )}
      <ProductSwitcherModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
      {enableSubscriptionUpgradeRequest && !globalProps.user?.isOwner && (
        <SubscriptionUpgradeRequestModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
        />
      )}
    </>
  );
};

export default ProfileButton;
